import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  importiereBuchungen,
  importZuruecksetzen,
  selectImportStatus,
} from 'modules/finanzen/features/finanzenSlice';
import { selectInvestitionen } from 'modules/investitionen/features/investitionenSlice';
import { IMPORT_QUELLEN, parseImport, dekodiere } from 'modules/finanzen/features/importParser';
import { letzterOrt, merkeOrt } from 'modules/finanzen/features/ort';
import OrtAuswahl from 'modules/finanzen/components/OrtAuswahl';
import BuchungPruefliste from 'modules/finanzen/components/BuchungPruefliste';

/**
 * Dialog zum Import eines Kontoauszugs. Ablauf: Quelle + Datei waehlen -> parsen ->
 * geparste Buchungen kontrollieren (links, mit Kategorie-Dropdown je Zeile; rechts
 * die zugehoerige Rohzeile der Datei) -> verschluesselt importieren. Doppelte
 * Buchungen (gleiche Transaktions-ID) werden beim Import automatisch uebersprungen.
 */
export default function ImportDialog({ onClose }) {
  const dispatch = useDispatch();
  const orte = useSelector(selectInvestitionen);
  const [quelleId, setQuelleId] = useState(IMPORT_QUELLEN[0].id);
  const [ort, setOrt] = useState(letzterOrt() || '');
  const [zeilen, setZeilen] = useState(null);
  const [markiert, setMarkiert] = useState(null);
  const [fehler, setFehler] = useState(null);

  const importStatus = useSelector(selectImportStatus);
  const fertig = importStatus.uebersprungen > 0 || (!importStatus.laeuft && importStatus.gesamt > 0);

  const schliessen = () => {
    dispatch(importZuruecksetzen());
    onClose();
  };

  const einlesen = async (datei) => {
    setFehler(null);
    try {
      const geparst = parseImport(quelleId, dekodiere(await datei.arrayBuffer()));
      if (geparst.length === 0) throw new Error('Keine Buchungen in der Datei gefunden.');
      setZeilen(geparst);
      setMarkiert(geparst[0].schluessel);
    } catch (ex) {
      setFehler(ex.message || 'Datei konnte nicht gelesen werden.');
    }
  };

  const setzeKategorie = (schluessel, kategorie) =>
    setZeilen((prev) => prev.map((z) => (z.schluessel === schluessel ? { ...z, kategorie } : z)));

  const setzeAktiv = (schluessel, aktiv) =>
    setZeilen((prev) => prev.map((z) => (z.schluessel === schluessel ? { ...z, aktiv } : z)));

  const alleSetzen = (aktiv) => setZeilen((prev) => prev.map((z) => ({ ...z, aktiv })));

  const importieren = async () => {
    const auswahl = zeilen.filter((z) => z.aktiv);
    if (auswahl.length === 0) return;
    if (orte.length > 0 && !ort) {
      setFehler('Bitte einen Investitionsort für die importierten Buchungen wählen.');
      return;
    }
    setFehler(null);
    // Alle importierten Buchungen dem gewaehlten Ort zuordnen (z. B. Trade Republic).
    const mitOrt = auswahl.map((z) => ({ ...z, ort: ort || null }));
    try {
      await dispatch(importiereBuchungen({ zeilen: mitOrt })).unwrap();
      merkeOrt(ort);
    } catch (ex) {
      setFehler('Import fehlgeschlagen. Bitte erneut versuchen.');
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="flex max-h-[90vh] w-full max-w-5xl flex-col rounded-2xl bg-paper shadow-xl">
        <DialogKopf onClose={schliessen} />
        <div className="min-h-0 flex-1 overflow-y-auto">
          {fertig ? (
            <ImportErgebnis status={importStatus} onClose={schliessen} />
          ) : importStatus.laeuft ? (
            <ImportFortschritt status={importStatus} />
          ) : zeilen ? (
            <BuchungPruefliste
              zeilen={zeilen}
              zielName={(orte.find((o) => o.uuid === ort) || {}).institut}
              markiert={markiert}
              onMarkieren={setMarkiert}
              onKategorie={setzeKategorie}
              onAktiv={setzeAktiv}
              onAlle={alleSetzen}
              onImport={importieren}
            />
          ) : (
            <QuellAnsicht
              quelleId={quelleId}
              onQuelle={setQuelleId}
              ort={ort}
              onOrt={setOrt}
              onDatei={einlesen}
              fehler={fehler}
            />
          )}
        </div>
        {fehler && zeilen && !importStatus.laeuft && !fertig && (
          <p className="border-t border-line px-6 py-2 text-sm text-red-700">{fehler}</p>
        )}
      </div>
    </div>
  );
}

/** Titelzeile des Dialogs. */
function DialogKopf({ onClose }) {
  return (
    <div className="flex items-center justify-between border-b border-line px-6 py-4">
      <h2 className="font-serif text-xl font-semibold text-ink">Kontoauszug importieren</h2>
      <button
        type="button"
        onClick={onClose}
        className="rounded-lg px-2 py-1 text-ink-muted transition-colors hover:bg-ink/5"
        aria-label="Schließen"
      >
        ✕
      </button>
    </div>
  );
}

/** Schritt 1: Quelle + Investitionsort waehlen und Datei hochladen. */
function QuellAnsicht({ quelleId, onQuelle, ort, onOrt, onDatei, fehler }) {
  const quelle = IMPORT_QUELLEN.find((q) => q.id === quelleId) || IMPORT_QUELLEN[0];
  return (
    <div className="space-y-5 p-6">
      <div>
        <label className="mb-1 block text-sm font-medium text-ink">Quelle</label>
        <select
          value={quelleId}
          onChange={(e) => onQuelle(e.target.value)}
          className="w-full rounded-lg border border-line bg-paper px-3 py-2 text-ink focus:border-brand-600 focus:outline-none"
        >
          {IMPORT_QUELLEN.map((q) => (
            <option key={q.id} value={q.id}>{q.label}</option>
          ))}
        </select>
        <p className="mt-1 text-xs text-ink-muted">
          Jede Bank hat ein eigenes Format – wähle passend zur Datei.
        </p>
      </div>
      <div>
        <label className="mb-1 block text-sm font-medium text-ink">Investitionsort</label>
        <OrtAuswahl wert={ort} onChange={onOrt} />
        <p className="mt-1 text-xs text-ink-muted">
          Alle importierten Buchungen werden diesem Ort zugeordnet.
        </p>
      </div>
      <div>
        <label className="mb-1 block text-sm font-medium text-ink">Datei ({quelle.dateiTyp})</label>
        <input
          type="file"
          accept={quelle.dateiTyp}
          onChange={(e) => e.target.files[0] && onDatei(e.target.files[0])}
          className="block w-full text-sm text-ink file:mr-3 file:rounded-lg file:border-0 file:bg-brand-600 file:px-4 file:py-2 file:text-sm file:font-medium file:text-white hover:file:bg-brand-500"
        />
      </div>
      {fehler && <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">{fehler}</p>}
      <p className="text-xs text-ink-muted">
        Die Datei wird nur im Browser gelesen. Alle Buchungen werden vor dem Speichern verschlüsselt.
      </p>
    </div>
  );
}

/** Fortschrittsanzeige waehrend des Speicherns. */
function ImportFortschritt({ status }) {
  const prozent = status.gesamt > 0 ? Math.round((status.fertig / status.gesamt) * 100) : 0;
  return (
    <div className="space-y-4 p-10 text-center">
      <p className="text-ink">Importiere und verschlüssle… {status.fertig} von {status.gesamt}</p>
      <div className="mx-auto h-2 w-full max-w-md overflow-hidden rounded-full bg-ink/10">
        <div className="h-full bg-brand-600 transition-all" style={{ width: `${prozent}%` }} />
      </div>
    </div>
  );
}

/** Abschluss-Zusammenfassung nach dem Import. */
function ImportErgebnis({ status, onClose }) {
  return (
    <div className="space-y-4 p-10 text-center">
      <p className="text-4xl">✅</p>
      <p className="text-ink">
        {status.gesamt} Buchung(en) importiert
        {status.uebersprungen > 0 && ` · ${status.uebersprungen} bereits vorhanden übersprungen`}.
      </p>
      <button
        type="button"
        onClick={onClose}
        className="rounded-lg bg-brand-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-brand-500"
      >
        Fertig
      </button>
    </div>
  );
}
